import React, { useState } from 'react';
import { Button, ButtonGroup, Form, ToggleButton } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getRecordsProgression } from '../api/trainer';
import { RecordDistance } from '../api/types';
import PageHeader from '../components/PageHeader';
import RecordsProgressChart from '../components/RecordsProgressChart';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import {
  API_TOP,
  buildSeries,
  distancesWithEfforts,
  minYearFor,
  ProgressMetric,
  ProgressView,
  RANGE_OPTIONS,
  TOP_OPTIONS,
  YearRange,
} from '../utils/progression';

type Mode = 'all' | 'single';

const RecordsProgressPage: React.FC = () => {
  const progress = useAsync(() => getRecordsProgression(API_TOP), []);
  const [view, setView] = useState<ProgressView>('yearly');
  const [mode, setMode] = useState<Mode>('all');
  const [top, setTop] = useState<number>(3);
  const [range, setRange] = useState<YearRange>('all');
  const [hidden, setHidden] = useState<Set<RecordDistance>>(() => new Set());
  const [singleCode, setSingleCode] = useState<RecordDistance | null>(null);

  const withEfforts = distancesWithEfforts(progress.data?.distances ?? []);
  const single = withEfforts.find(d => d.distance === singleCode) ?? withEfforts[0] ?? null;
  const metric: ProgressMetric = mode === 'single' ? 'time' : 'pace';

  // Built from every distance with data, so a distance keeps its color when another is hidden.
  const allSeries = buildSeries(withEfforts, {
    view,
    top,
    metric,
    minYear: minYearFor(range, new Date().getFullYear()),
  });
  const visible = allSeries.filter(s =>
    mode === 'single' ? s.code === single?.distance : !hidden.has(s.code)
  );

  const toggle = (code: RecordDistance) => {
    setHidden(current => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const hasData = withEfforts.length > 0;

  return (
    <>
      <PageHeader title="Progression">
        <p>
          Your best running efforts over time. Each dot is one of the top efforts of a year, and the thick line joins the
          yearly bests. Adjusted times and manual times count too.
        </p>
        <div>
          <Link to="/records" className="btn btn-outline-secondary btn-sm">
            Back to records
          </Link>
        </div>
      </PageHeader>

      <StatusMessage loading={progress.loading} error={progress.error} onRetry={progress.reload} />

      {progress.data && !hasData && (
        <section className="surface">
          <p className="mb-0">
            No running efforts yet. Efforts appear once your running activities are synced, or when you add a manual time
            on the Records page.
          </p>
        </section>
      )}

      {progress.data && hasData && (
        <section className="surface">
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <ButtonGroup aria-label="Chart view">
              <ToggleButton
                id="progress-view-yearly"
                type="radio"
                variant="outline-primary"
                name="progress-view"
                value="yearly"
                checked={view === 'yearly'}
                onChange={() => setView('yearly')}
                size="sm"
              >
                Yearly best
              </ToggleButton>
              <ToggleButton
                id="progress-view-pb"
                type="radio"
                variant="outline-primary"
                name="progress-view"
                value="pb"
                checked={view === 'pb'}
                onChange={() => setView('pb')}
                size="sm"
              >
                All-time PB progression
              </ToggleButton>
            </ButtonGroup>

            <ButtonGroup aria-label="Distances shown">
              <ToggleButton
                id="progress-mode-all"
                type="radio"
                variant="outline-secondary"
                name="progress-mode"
                value="all"
                checked={mode === 'all'}
                onChange={() => setMode('all')}
                size="sm"
              >
                All distances
              </ToggleButton>
              <ToggleButton
                id="progress-mode-single"
                type="radio"
                variant="outline-secondary"
                name="progress-mode"
                value="single"
                checked={mode === 'single'}
                onChange={() => setMode('single')}
                size="sm"
              >
                Single distance
              </ToggleButton>
            </ButtonGroup>

            {mode === 'single' && single && (
              <Form.Select
                size="sm"
                className="w-auto"
                aria-label="Distance"
                value={single.distance}
                onChange={e => setSingleCode(e.target.value as RecordDistance)}
              >
                {withEfforts.map(d => (
                  <option key={d.distance} value={d.distance}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
            )}

            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Efforts per year"
              value={top}
              disabled={view === 'pb'}
              onChange={e => setTop(Number(e.target.value))}
              title={view === 'pb' ? 'Applies to the yearly view' : undefined}
            >
              {TOP_OPTIONS.map(n => (
                <option key={n} value={n}>
                  Top {n} per year
                </option>
              ))}
            </Form.Select>

            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Year range"
              value={range}
              onChange={e => setRange(e.target.value as YearRange)}
            >
              {RANGE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {mode === 'all' && (
            <div className="progress-legend mb-3" role="group" aria-label="Distances">
              {allSeries.map(s => {
                const on = !hidden.has(s.code);
                return (
                  <Button
                    key={s.code}
                    size="sm"
                    variant="outline-secondary"
                    className={on ? '' : 'opacity-50'}
                    aria-pressed={on}
                    onClick={() => toggle(s.code)}
                  >
                    <span
                      className="progress-swatch"
                      style={{ background: s.style.color }}
                      aria-hidden="true"
                    />
                    {s.name}
                  </Button>
                );
              })}
            </div>
          )}

          {visible.length === 0 ? (
            <p className="muted mb-0">
              {mode === 'all'
                ? 'No distance is shown. Press a distance above to show it, or change the year range.'
                : 'No efforts for this distance in the chosen range.'}
            </p>
          ) : (
            <RecordsProgressChart series={visible} view={view} metric={metric} />
          )}
        </section>
      )}
    </>
  );
};

export default RecordsProgressPage;
